/**
 * WHAT TRAVELS ON A ROOM'S DATA CHANNEL, stated once — because two programs now write it.
 *
 * `chat.ts` refuses to hold a wire format and says why: where a chat lives is left open (§8.1), so
 * a packet shape in there would decide it. A ROOM's channel is not open in that way. D12 settled
 * it — the join token already grants the data channel, which is why live chat needs no gateway —
 * so what a room's channel carries is a decision already made, and this is where it is written.
 *
 * The one thing on it that is not chat is a NUDGE: *this room changed, ask the server what it is
 * now*. It carries no state on purpose. A packet that said what changed would be a second source
 * of truth about the room, arriving over a wire, and a forged one would be worth forging; this one
 * costs a reader one read of a route they may already call and can say nothing false.
 *
 * It is HERE rather than in the browser client because the SERVER sends it. A change made by
 * somebody who is not in the room — the second Hall's leadership accepting a co-host invitation, a
 * site moderator acting from the moderation screen — reached nobody at all while the nudge was the
 * browser's to send: the announcement depended on the actor being present, which is a fact about
 * the actor and not about the change.
 */

/** The one kind of packet on this channel that is not a chat message. */
export const ROOM_CHANGED = 'changed';

/**
 * The nudge, as bytes.
 *
 * `at` is passed in rather than read from the clock here, so the caller owns the time and a test
 * can state it. Readers do not trust it either way: the browser stamps arrival itself, for the
 * same reason it does with chat.
 */
export function roomChangedPacket(at: number): Uint8Array {
  return new TextEncoder().encode(JSON.stringify({ kind: ROOM_CHANGED, at }));
}
